/**
 * Product Upsell
 * Ajoute les produits upsell sur les fiches produit
 * Modifie la requête cart/add pour inclure tous les items en un seul call
 */

(function() {
  'use strict';

  const SELECTORS = {
    container: '.product-upsell',
    item: '.product-upsell__item',
    checkbox: '[data-upsell-checkbox]',
    variantSelect: '[data-upsell-variant-select]',
    priceContainer: '[data-upsell-price]',
    itemImage: '.product-upsell__image img'
  };

  window._upsell = {
    productHandle: null,
    getCheckedUpsells: null
  };

  class ProductUpsell {
    constructor(container) {
      this.container = container;
      this.productHandle = container.dataset.productHandle;
      
      window._upsell.productHandle = this.productHandle;
      window._upsell.getCheckedUpsells = () => this.getCheckedUpsells();
      
      this.init();
    }

    init() {
      this.bindEvents();
      this.initCheckboxStates();
    }

    initCheckboxStates() {
      const checkboxes = this.container.querySelectorAll(SELECTORS.checkbox);
      checkboxes.forEach(checkbox => {
        this.updateItemState(checkbox);
      });
    }

    bindEvents() {
      // Bind variant selects (generic index-based)
      const variantSelects = this.container.querySelectorAll('[data-upsell-variant-select]');
      variantSelects.forEach(select => {
        if (select.tagName === 'SELECT') {
          select.addEventListener('change', (e) => this.onVariantChange(e));
        }
      });

      const checkboxes = this.container.querySelectorAll(SELECTORS.checkbox);
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => this.onCheckboxChange(e));
      });

      // Make entire card clickable to toggle checkbox
      const items = this.container.querySelectorAll(SELECTORS.item);
      items.forEach(item => {
        item.addEventListener('click', (e) => {
          if (e.target.closest('select, option, input[type="checkbox"], label')) return;
          const checkbox = item.querySelector(SELECTORS.checkbox);
          if (checkbox) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    onVariantChange(e) {
      const select = e.target;
      const selectedOption = select.options[select.selectedIndex];
      const index = select.dataset.upsellVariantSelect;
      const checkbox = this.container.querySelector(`[data-upsell-checkbox="${index}"]`);
      
      if (checkbox) {
        checkbox.dataset.variantId = select.value;
      }

      this.updatePrice(selectedOption, index);
      this.updateImage(selectedOption, index);
    }

    updatePrice(option, index) {
      const priceContainer = this.container.querySelector(`[data-upsell-price="${index}"]`);
      if (!priceContainer) return;

      const price = parseInt(option.dataset.price, 10);
      const comparePrice = parseInt(option.dataset.comparePrice, 10);

      let html = '';
      if (comparePrice && comparePrice > price) {
        const discount = Math.round((comparePrice - price) / comparePrice * 100);
        html = `
          <span class="product-upsell__price-compare">+${this.formatMoney(price)}</span>
          <span class="product-upsell__price-sale">+${this.formatMoney(price)}</span>
          <span class="product-upsell__badge">-${discount}%</span>
        `;
      } else {
        html = `<span class="product-upsell__price-regular">+${this.formatMoney(price)}</span>`;
      }

      priceContainer.innerHTML = html;
    }

    updateImage(option, index) {
      const imageUrl = option.dataset.image;
      if (!imageUrl) return;

      const item = this.container.querySelector(`[data-upsell-index="${index}"]`);
      const img = item?.querySelector(SELECTORS.itemImage);
      if (img) {
        img.src = imageUrl;
      }
    }

    formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    onCheckboxChange(e) {
      this.updateItemState(e.target);
    }

    updateItemState(checkbox) {
      const item = checkbox.closest(SELECTORS.item);
      if (item) {
        item.classList.toggle('is-checked', checkbox.checked);
      }
    }

    getCheckedUpsells() {
      const upsells = [];
      const checkboxes = this.container.querySelectorAll(SELECTORS.checkbox + ':checked');
      
      checkboxes.forEach(checkbox => {
        const index = checkbox.dataset.upsellCheckbox;
        let variantId = checkbox.dataset.variantId;
        const handle = checkbox.dataset.upsellHandle;

        // Check if there's a variant select for this index
        const select = this.container.querySelector(`[data-upsell-variant-select="${index}"]`);
        if (select && select.tagName === 'SELECT') {
          variantId = select.value;
        }

        if (variantId) {
          upsells.push({ index, variantId, handle });
        }
      });

      return upsells;
    }
  }

  // =====================================================
  // HELPER: Créer les upsell items
  // =====================================================
  
  function getUpsellItemsArray() {
    if (!window._upsell.getCheckedUpsells) return [];
    
    const upsells = window._upsell.getCheckedUpsells();
    
    return upsells.map(upsell => ({
      id: parseInt(upsell.variantId, 10),
      quantity: 1,
      properties: {
        '_upsell': 'true',
        '_upsell_source': window._upsell.productHandle,
        '_upsell_handle': upsell.handle
      }
    }));
  }

  // =====================================================
  // HELPER: Convertir en format multi-items URL-encoded
  // =====================================================
  
  function buildMultiItemsBodyUrlEncoded(originalBody, upsells) {
    const params = new URLSearchParams(originalBody);
    const mainId = params.get('id');
    const mainQuantity = params.get('quantity') || '1';
    
    if (!mainId) return null;
    
    const newParams = new URLSearchParams();
    
    // Item 0 : produit principal
    newParams.append('items[0][id]', mainId);
    newParams.append('items[0][quantity]', mainQuantity);
    
    // Items suivants : upsells
    upsells.forEach((upsell, index) => {
      const i = index + 1;
      newParams.append(`items[${i}][id]`, upsell.variantId);
      newParams.append(`items[${i}][quantity]`, '1');
      newParams.append(`items[${i}][properties][_upsell]`, 'true');
      newParams.append(`items[${i}][properties][_upsell_source]`, window._upsell.productHandle);
      newParams.append(`items[${i}][properties][_upsell_handle]`, upsell.handle);
    });
    
    // Conserver certains params utiles pour iCart
    if (params.get('section-id')) {
      newParams.append('section-id', params.get('section-id'));
    }
    if (params.get('product-id')) {
      newParams.append('product-id', params.get('product-id'));
    }
    
    return newParams.toString();
  }

  // =====================================================
  // INTERCEPTION DE XMLHttpRequest (pour iCart)
  // =====================================================
  // Garde d'idempotence : le script peut s'exécuter plusieurs fois (balise <script>
  // clonée depuis le template quick-buy modal) — sans garde, fetch/XHR seraient
  // wrappés en double et les upsells ajoutés deux fois.
  if (!window.__lcfUpsellPatched) {
  window.__lcfUpsellPatched = true;

  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._method = method;
    this._url = url;
    return originalXHROpen.call(this, method, url, ...rest);
  };
  
  XMLHttpRequest.prototype.send = function(body) {
    if (this._method === 'POST' && this._url && this._url.includes('/cart/add')) {
      // URL-encoded body
      if (typeof body === 'string' && body.includes('=') && !body.startsWith('{')) {
        let upsellsToAdd = [];
        if (window._upsell.getCheckedUpsells) {
          upsellsToAdd = window._upsell.getCheckedUpsells();
        }
        
        if (upsellsToAdd.length > 0) {
          const newBody = buildMultiItemsBodyUrlEncoded(body, upsellsToAdd);
          if (newBody) {
            return originalXHRSend.call(this, newBody);
          }
        }
      }
    }
    
    return originalXHRSend.call(this, body);
  };

  // =====================================================
  // INTERCEPTION DE FETCH (pour Quick Buy et thème)
  // =====================================================
  
  const originalFetch = window.fetch;
  
  window.fetch = async function(url, options = {}) {
    const urlString = typeof url === 'string' ? url : url.toString();
    
    if (urlString.includes('/cart/add') && options.method === 'POST') {
      const upsellItems = getUpsellItemsArray();
      
      if (upsellItems.length > 0 && options.body) {
        let mainId = null;
        let mainQuantity = 1;
        let mainProperties = null;
        
        // CAS 1: FormData
        if (options.body instanceof FormData) {
          mainId = options.body.get('id');
          mainQuantity = parseInt(options.body.get('quantity'), 10) || 1;
        }
        // CAS 2: JSON string
        else if (typeof options.body === 'string' && options.body.startsWith('{')) {
          try {
            const data = JSON.parse(options.body);
            if (data.id) {
              mainId = data.id;
              mainQuantity = data.quantity || 1;
              mainProperties = data.properties;
            } else if (data.items && data.items.length > 0) {
              // Déjà un format items, on ajoute les upsells
              // (sauf s'ils y sont déjà — payload déjà réécrit par un autre wrapper)
              const alreadyHasUpsells = data.items.some(item => item.properties && item.properties._upsell);
              if (alreadyHasUpsells) {
                return originalFetch.call(window, url, options);
              }
              data.items.push(...upsellItems);
              
              const newOptions = {
                ...options,
                headers: {
                  ...options.headers,
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify(data)
              };
              
              return originalFetch.call(window, url, newOptions);
            }
          } catch (e) {
            // Pas du JSON valide
          }
        }
        // CAS 3: URL-encoded string
        else if (typeof options.body === 'string' && options.body.includes('=')) {
          const params = new URLSearchParams(options.body);
          mainId = params.get('id');
          mainQuantity = parseInt(params.get('quantity'), 10) || 1;
        }
        
        // Si on a trouvé un mainId, construire le nouveau payload
        if (mainId) {
          const items = [{
            id: parseInt(mainId, 10),
            quantity: mainQuantity
          }];
          
          if (mainProperties) {
            items[0].properties = mainProperties;
          }
          
          items.push(...upsellItems);
          
          const newOptions = {
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items })
          };
          
          return originalFetch.call(window, url, newOptions);
        }
      }
    }
    
    return originalFetch.call(window, url, options);
  };

  } // fin garde __lcfUpsellPatched

  // =====================================================
  // INITIALISATION
  // =====================================================

  function init() {
    const containers = document.querySelectorAll(SELECTORS.container);
    // Marqueur anti double-init : es-module-shims re-dispatche DOMContentLoaded,
    // et le script peut se ré-exécuter (modal quick-buy) — un double bind
    // ferait toggler la checkbox deux fois au clic sur la carte (clic mort).
    if (containers.length > 0 && !containers[0].dataset.upsellBound) {
      containers[0].dataset.upsellBound = '1';
      new ProductUpsell(containers[0]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();